import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/context/AuthContext';
import { getProfile, updateProfile } from '@/lib/profileApi';
import { Camera, User as UserIcon } from 'lucide-react';
import { useTheme } from 'next-themes';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

const TIMEZONES = (() => {
  try {
    return Intl.supportedValuesOf('timeZone');
  } catch {
    return ['Europe/London', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'Asia/Kolkata', 'Asia/Singapore', 'Australia/Sydney'];
  }
})();

const ProfileTab = () => {
  const { user } = useAuth();
  const { theme, setTheme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const [form, setForm] = useState({
    name: '',
    phone: '',
    company_name: '',
    job_title: '',
    business_type: '',
    timezone: 'Europe/London',
  });
  const [initial, setInitial] = useState(null);

  const loadProfile = useCallback(async () => {
    try {
      const data = await getProfile();
      const loaded = {
        name: data.name || data.display_name || '',
        phone: data.phone || '',
        company_name: data.company_name || '',
        job_title: data.job_title || '',
        business_type: data.business_type || '',
        timezone: data.timezone || 'Europe/London',
      };
      setForm(loaded);
      setInitial(loaded);
    } catch (err) {
      toast.error(err.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  useEffect(() => {
    if (!initial) return;
    const changed = Object.keys(form).some(k => form[k] !== initial[k]);
    setDirty(changed);
  }, [form, initial]);

  // Unsaved changes guard
  useEffect(() => {
    const handler = (e) => {
      if (dirty) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  const setField = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    if (!dirty) return;
    setSaving(true);
    try {
      await updateProfile(form);
      setInitial({ ...form });
      setDirty(false);
      toast.success('Profile updated successfully');
    } catch (err) {
      toast.error(err.message || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="w-20 h-20 rounded-full" />
          <div className="space-y-2"><Skeleton className="w-40 h-4" /><Skeleton className="w-24 h-3" /></div>
        </div>
        {[1, 2, 3, 4].map(i => (<div key={i} className="space-y-2"><Skeleton className="w-24 h-4" /><Skeleton className="w-full max-w-md h-10" /></div>))}
      </div>
    );
  }

  const initials = (form.name || user?.email || '?').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);

  return (
    <div className="space-y-8">
      {/* Avatar */}
      <div className="flex items-center gap-5">
        <div className="relative group">
          <div className="w-20 h-20 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-xl font-semibold">
            {initials || <UserIcon className="w-8 h-8" />}
          </div>
          <button
            className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
            title="Upload photo (coming soon)"
            onClick={() => toast.info('Avatar upload coming soon')}
          >
            <Camera className="w-5 h-5 text-white" />
          </button>
        </div>
        <div>
          <p className="font-medium">{form.name || 'Your Name'}</p>
          <p className="text-sm text-muted-foreground">{user?.email}</p>
        </div>
      </div>

      {/* Personal Information */}
      <div>
        <h3 className="text-lg font-medium mb-4">Personal Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
          <div>
            <Label htmlFor="profile-name">Full Name</Label>
            <Input id="profile-name" value={form.name} onChange={e => setField('name', e.target.value)} placeholder="John Doe" className="mt-2" />
          </div>
          <div>
            <Label htmlFor="profile-email">Email</Label>
            <Input id="profile-email" type="email" value={user?.email || ''} disabled className="mt-2" />
            <p className="text-xs text-muted-foreground mt-1">Contact support to change your email</p>
          </div>
          <div>
            <Label htmlFor="profile-phone">Phone</Label>
            <Input id="profile-phone" type="tel" value={form.phone} onChange={e => setField('phone', e.target.value)} placeholder="+44 7700 900000" className="mt-2" />
          </div>
          <div>
            <Label htmlFor="profile-job-title">Job Title</Label>
            <Input id="profile-job-title" value={form.job_title} onChange={e => setField('job_title', e.target.value)} placeholder="Finance Director" className="mt-2" />
          </div>
        </div>
      </div>

      <hr className="border-border" />

      {/* Organisation */}
      <div>
        <h3 className="text-lg font-medium mb-4">Organisation</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
          <div>
            <Label htmlFor="profile-company">Company / Firm Name</Label>
            <Input id="profile-company" value={form.company_name} onChange={e => setField('company_name', e.target.value)} placeholder="Acme Ltd" className="mt-2" />
          </div>
          <div>
            <Label htmlFor="profile-timezone">Timezone</Label>
            <Select value={form.timezone} onValueChange={v => setField('timezone', v)}>
              <SelectTrigger id="profile-timezone" className="mt-2">
                <SelectValue placeholder="Select timezone" />
              </SelectTrigger>
              <SelectContent className="max-h-60">
                {TIMEZONES.map(tz => <SelectItem key={tz} value={tz}>{tz.replace(/_/g, ' ')}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label>Account Type</Label>
            <RadioGroup value={form.business_type} onValueChange={v => setField('business_type', v)} className="flex flex-col sm:flex-row gap-4 mt-2">
              {[
                { value: 'company', label: 'Company', desc: 'Filing for my own company' },
                { value: 'firm', label: 'Accounting Firm', desc: 'Filing on behalf of clients' },
                { value: 'group_company', label: 'Group Company', desc: 'Managing subsidiaries' },
              ].map(opt => (
                <label key={opt.value} className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors flex-1 ${form.business_type === opt.value ? 'border-primary bg-primary/5' : 'border-border hover:border-muted-foreground/30'}`}>
                  <RadioGroupItem value={opt.value} className="mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">{opt.label}</p>
                    <p className="text-xs text-muted-foreground">{opt.desc}</p>
                  </div>
                </label>
              ))}
            </RadioGroup>
          </div>
        </div>
      </div>

      <hr className="border-border" />

      {/* Appearance */}
      <div>
        <h3 className="text-lg font-medium mb-4">Appearance</h3>
        <div className="flex gap-3">
          {[
            { value: 'light', label: 'Light' },
            { value: 'dark', label: 'Dark' },
            { value: 'system', label: 'System' },
          ].map(opt => (
            <button
              key={opt.value}
              onClick={() => setTheme(opt.value)}
              className={`px-4 py-2 rounded-md border text-sm font-medium transition-colors ${theme === opt.value ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:border-muted-foreground/30'}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Save */}
      <div className="flex items-center gap-3 pt-2">
        <Button onClick={handleSave} disabled={!dirty || saving}>
          {saving ? 'Saving\u2026' : 'Save Changes'}
        </Button>
        {dirty && <p className="text-xs text-muted-foreground">You have unsaved changes</p>}
      </div>
    </div>
  );
};

export default ProfileTab;
